import type { StoreSettings } from '@/hooks/useStoreSettings';

interface PaymentMethodSelectorProps {
  selected: 'cash' | 'bank';
  onChange: (method: 'cash' | 'bank') => void;
  settings: StoreSettings;
}

export default function PaymentMethodSelector({ selected, onChange, settings }: PaymentMethodSelectorProps) {
  const { paymentCashEnabled, paymentBankEnabled } = settings;

  return (
    <div className="rounded-2xl border border-white/5 bg-[#111] p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-400/15 rounded-full">
          <i className="ri-bank-card-line text-amber-400 text-sm"></i>
        </div>
        <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
          طريقة الدفع
        </h3>
      </div>

      <div className="flex flex-col gap-3">
        {/* Cash on Delivery */}
        {paymentCashEnabled && (
          <button
            type="button"
            onClick={() => onChange('cash')}
            className={`w-full flex items-center gap-4 rounded-xl border-2 px-4 py-4 transition-all cursor-pointer text-right ${
              selected === 'cash'
                ? 'border-green-400/60 bg-green-400/8'
                : 'border-white/8 bg-[#1a1a1a] hover:border-white/20'
            }`}
          >
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
              selected === 'cash' ? 'bg-green-400/20' : 'bg-white/5'
            }`}>
              <i className={`ri-money-dollar-circle-line text-xl ${selected === 'cash' ? 'text-green-400' : 'text-gray-500'}`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${selected === 'cash' ? 'text-white' : 'text-gray-300'}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                الدفع عند الاستلام
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                ادفع نقداً عند وصول طلبك — لا حاجة لبطاقة
              </p>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
              selected === 'cash' ? 'border-green-400 bg-green-400' : 'border-gray-600'
            }`}>
              {selected === 'cash' && <i className="ri-check-line text-black text-[10px]"></i>}
            </div>
          </button>
        )}

        {/* Bank Transfer */}
        {paymentBankEnabled && (
          <button
            type="button"
            onClick={() => onChange('bank')}
            className={`w-full flex items-center gap-4 rounded-xl border-2 px-4 py-4 transition-all cursor-pointer text-right ${
              selected === 'bank'
                ? 'border-amber-400/60 bg-amber-400/8'
                : 'border-white/8 bg-[#1a1a1a] hover:border-white/20'
            }`}
          >
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
              selected === 'bank' ? 'bg-amber-400/20' : 'bg-white/5'
            }`}>
              <i className={`ri-bank-line text-xl ${selected === 'bank' ? 'text-amber-400' : 'text-gray-500'}`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold ${selected === 'bank' ? 'text-white' : 'text-gray-300'}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                التحويل البنكي
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                حوّل المبلغ مسبقاً وأرسل إيصال التحويل
              </p>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
              selected === 'bank' ? 'border-amber-400 bg-amber-400' : 'border-gray-600'
            }`}>
              {selected === 'bank' && <i className="ri-check-line text-black text-[10px]"></i>}
            </div>
          </button>
        )}
      </div>

      {/* Bank Details Card — shown when bank transfer is selected */}
      {selected === 'bank' && paymentBankEnabled && (
        <div className="rounded-xl border border-amber-400/20 bg-[#1a1a1a] overflow-hidden">
          <div className="bg-gradient-to-r from-amber-400/15 to-amber-400/5 px-4 py-3 border-b border-amber-400/15">
            <p className="text-amber-400 text-xs font-bold flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-information-line"></i>
              بيانات الحساب البنكي للتحويل
            </p>
          </div>
          <div className="p-4 flex flex-col gap-3">
            {settings.bankName && (
              <BankInfoRow icon="ri-bank-line" label="البنك" value={settings.bankName} />
            )}
            {settings.bankAccountName && (
              <BankInfoRow icon="ri-user-line" label="اسم الحساب" value={settings.bankAccountName} />
            )}
            {settings.bankAccountNumber && (
              <BankInfoRow icon="ri-hashtag" label="رقم الحساب" value={settings.bankAccountNumber} mono copyable />
            )}
            {settings.bankIban && (
              <BankInfoRow icon="ri-global-line" label="IBAN" value={settings.bankIban} mono copyable />
            )}
            {!settings.bankName && !settings.bankAccountName && !settings.bankAccountNumber && !settings.bankIban && (
              <p className="text-gray-500 text-xs text-center py-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                لم يتم إعداد بيانات البنك بعد — يرجى التواصل مع المتجر
              </p>
            )}
          </div>
          <div className="bg-amber-400/5 border-t border-amber-400/10 px-4 py-3">
            <p className="text-amber-400/80 text-[11px] flex items-start gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-alert-line flex-shrink-0 mt-0.5"></i>
              بعد إتمام الطلب، سيتواصل معك فريقنا عبر واتساب لتأكيد استلام التحويل وشحن طلبك
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

interface BankInfoRowProps {
  icon: string;
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}

function BankInfoRow({ icon, label, value, mono = false, copyable = false }: BankInfoRowProps) {
  const handleCopy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className={`${icon} text-gray-500 text-sm`}></i>
        </div>
        <span className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={`text-white text-xs font-bold ${mono ? 'font-mono' : ''}`}
          dir={mono ? 'ltr' : 'rtl'}
          style={mono ? {} : { fontFamily: "'Cairo', sans-serif" }}
        >
          {value}
        </span>
        {copyable && (
          <button
            type="button"
            onClick={handleCopy}
            className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-amber-400 transition-colors cursor-pointer rounded-md hover:bg-amber-400/10"
            title="نسخ"
          >
            <i className="ri-file-copy-line text-xs"></i>
          </button>
        )}
      </div>
    </div>
  );
}
